import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { KakoCoin } from '../oil/OilIcons';
import { wbApi, fmt } from './wbApi';
import { WbPodium, WbRankList, WbRulesBtn } from './WbPodium';

const Spinner = () => <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>;

/* Regras do ranking: quem mais estoura balões leva os prêmios fixos. */
const Regras = ({ prizes = [], config = {}, onClose }) => createPortal(
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={onClose} data-testid="wb-rank-rules">
    <div className="relative w-full max-w-sm rounded-3xl border border-amber-500/50 bg-[#140a22] p-4 text-purple-100" onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose} className="absolute right-3 top-3 rounded-full bg-black/40 p-1.5 text-white/70" data-testid="wb-rank-rules-close" aria-label="Fechar"><X className="h-4 w-4" /></button>
      <p className="wb-title text-2xl text-orange-300">Ranking dos Bruxos</p>
      <ul className="mt-2 space-y-1.5 text-[11px] leading-relaxed">
        <li>▸ Conta <b className="text-amber-300">quantos balões você estourou</b> no evento — não importa se saiu prêmio ou não.</li>
        <li>▸ Cada balão custa <b className="text-amber-300">{fmt(config.pop_cost || 500)}</b> Moedas da Bruxa; você ganha <b className="text-amber-300">{fmt(100000 * Number(config.rate_bp || 50) / 10000)}</b> a cada 100.000 moedas recarregadas.</li>
        <li>▸ Empate: quem chegou primeiro à marca fica na frente.</li>
        <li>▸ No fim do evento os prêmios são liberados direto na conta KAKO dos vencedores:</li>
      </ul>
      <div className="mt-2 space-y-1" data-testid="wb-rank-prize-split">
        {prizes.map((p, i) => (
          <div key={i} className="flex items-center justify-between rounded-xl bg-black/40 px-3 py-1.5 text-xs" data-testid={`wb-rank-prize-row-${i + 1}`}>
            <span className="font-black text-purple-200">{i + 1}º lugar</span>
            <span className="flex items-center gap-1 font-black text-amber-200"><KakoCoin className="h-4" /> {fmt(p)} moedas</span>
          </div>
        ))}
      </div>
    </div>
  </div>, document.body);

/* Ranking único de clientes (Bruxos) por balões estourados, com prêmios fixos no Top. */
export const WbRankings = ({ tick, me, config }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState(false);
  useEffect(() => {
    let alive = true;
    wbApi.get('/rankings').then((r) => { if (alive) { setData(r.data); setLoading(false); } }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tick]);
  const rows = data?.rows || [];
  const prizes = data?.prizes || config?.ranking_prizes || [];
  const prize = (rank) => (prizes[rank - 1] > 0 ? `${fmt(prizes[rank - 1])} moedas` : null);
  const myRank = data?.my_rank || me?.rank;
  return (
    <div className="space-y-2" data-testid="wb-rankings">
      <div className="relative">
        <img src="/wb/title_bruxos.webp" alt="RANKING DOS BRUXOS" className="mx-auto w-[240px] max-w-full drop-shadow-xl" />
        <div className="absolute right-0 top-0"><WbRulesBtn onClick={() => setRules(true)} /></div>
      </div>
      <p className="text-center text-[10px] font-black uppercase tracking-[.25em] text-purple-200/80">quem mais estoura balões leva</p>
      <div className="mx-auto flex w-fit gap-2">
        {prizes.slice(0, 3).map((p, i) => (
          <span key={i} className="flex items-center gap-1 rounded-full border border-amber-500/50 bg-black/60 px-3 py-1 text-[10px] font-black text-amber-200" data-testid={`wb-rank-top-prize-${i + 1}`}>
            {i + 1}º <KakoCoin className="h-3.5" /> {fmt(p)}
          </span>
        ))}
      </div>
      {loading ? <Spinner /> : (
        <>
          <WbPodium kind="bruxos" rows={rows} label={(r) => r.nickname} value={(r) => `${fmt(r.pops)} balões`} prize={prize} testid="wb-rank-podium" />
          <WbRankList rows={rows.slice(3)} startAt={4} label={(r) => r.nickname} value={(r) => `${fmt(r.pops)} balões`} prize={prize} testid="wb-rank-list"
            empty="Ninguém estourou balões ainda. Seja o primeiro!" />
        </>
      )}
      {me && (
        <div className="rounded-2xl border border-purple-800/60 bg-black/50 px-4 py-2 text-center text-xs text-purple-100" data-testid="wb-my-rank">
          Sua posição: <b className="text-lime-300">{myRank ? `${myRank}º` : '—'}</b> · <b className="text-amber-200">{fmt(me.pops_total)}</b> balões estourados
        </div>
      )}
      {rules && <Regras prizes={prizes} config={config} onClose={() => setRules(false)} />}
    </div>
  );
};
